'use client'

import { useCallback, useState } from 'react'

import { ErrorItem, HttpStatus, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { setLocalStorageItem } from '@marketplace-web/shared/browser'
import {
  BusinessRegistrationModel,
  validateBusinessRegistrationDsa,
  transformBusinessRegistrationToDto,
} from '@marketplace-web/domain/business-accounts-data'

import { REGISTRATION_FORM_STORAGE_KEY } from '../../../constants'

export type OnValidateRegistrationParams = {
  registrationFields: Partial<BusinessRegistrationModel>
  validate: boolean
  onSuccess?: () => void
}

const useUpdateRegistration = (): {
  showGeneralError: boolean
  onUpdateRegistration: ({
    registrationFields,
    validate,
  }: OnValidateRegistrationParams) => Promise<void>
  loading: boolean
  errors: Array<ErrorItem> | null
} => {
  const [showGeneralError, setShowGeneralError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Array<ErrorItem> | null>(null)

  const onUpdateRegistration = useCallback(
    async ({ registrationFields, validate, onSuccess }: OnValidateRegistrationParams) => {
      const existingFields = JSON.parse(localStorage.getItem(REGISTRATION_FORM_STORAGE_KEY) || '{}')
      const updatedFields = { ...existingFields, ...registrationFields }
      setLocalStorageItem(REGISTRATION_FORM_STORAGE_KEY, JSON.stringify(updatedFields))

      if (!validate) {
        return
      }

      try {
        setErrors(null)
        setShowGeneralError(false)
        setLoading(true)

        const response = await validateBusinessRegistrationDsa(
          transformBusinessRegistrationToDto(registrationFields),
        )

        if ('errors' in response) {
          setErrors(response.errors)

          if (
            response.code === ResponseCode.ServerError ||
            response.status === HttpStatus.InternalError ||
            response.status === HttpStatus.TooManyRequests ||
            response.status === HttpStatus.UnprocessableEntity
          ) {
            setShowGeneralError(true)
          }
        } else {
          onSuccess?.()
        }
      } catch (error) {
        setErrors(error)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return {
    loading,
    errors,
    showGeneralError,
    onUpdateRegistration,
  }
}

export default useUpdateRegistration
